// Per-dialect collation / character-set synthesizer (v2 Slice A4).
//
// PRD: Ralph/DdlStructuralEdit2/PRD.md § Slice A4.
//
// Each engine spells the COLLATE clause differently:
//   - mssql:  `COLLATE <name>`         — name is unquoted (e.g.
//             `Latin1_General_CI_AS`).
//   - pgsql:  `COLLATE "<name>"`       — name is double-quoted; ICU and libc
//             collation names alike (e.g. `"en-US-x-icu"`, `"C"`).
//   - mysql:  `[CHARACTER SET <cs>] [COLLATE <name>]` — both fields
//             independent; at least one must be set. Charset comes first
//             when both are present (mysql parses both orderings but the
//             reference manual lists CHARACTER SET first).
//   - oracle: `COLLATE <name>`         — column-level collation requires
//             `MAX_STRING_SIZE = EXTENDED` and 12.2+; the synthesizer emits
//             the clause and lets the engine reject if not configured.
//   - sqlite: `COLLATE <name>`         — built-in collations are `BINARY`,
//             `NOCASE`, `RTRIM`; user-defined collations also work.
//
// `charset` is mysql-only. Every other engine throws when it's set.
// `name` is required on every engine except mysql (where mysql accepts
// `CHARACTER SET <cs>` alone). An empty spec (no name + no charset) throws.

import type { SqlEngine } from '../../../sql-grammar/model';
import type { CollationSpec } from '../../types';

export function formatCollationSpec(engine: SqlEngine, collation: CollationSpec): string {
  if (collation.name === undefined && collation.charset === undefined) {
    throw new Error(
      `applyDdlEdit: CollationSpec requires at least 'name' or (mysql) `
        + `'charset' — got an empty spec`,
    );
  }
  if (engine === 'mssql') return formatMssql(collation);
  if (engine === 'postgres') return formatPgsql(collation);
  if (engine === 'mysql') return formatMysql(collation);
  if (engine === 'oracle') return formatOracle(collation);
  return formatSqlite(collation);
}

function rejectCharsetOutsideMysql(engine: string, collation: CollationSpec): void {
  if (collation.charset !== undefined) {
    throw new Error(
      `applyDdlEdit: ${engine} does not accept 'charset' on CollationSpec — `
        + `CHARACTER SET is a mysql-only column attribute`,
    );
  }
}

function requireName(engine: string, collation: CollationSpec): string {
  if (collation.name === undefined) {
    throw new Error(
      `applyDdlEdit: ${engine} CollationSpec requires 'name' — only mysql `
        + `accepts a bare CHARACTER SET without a COLLATE clause`,
    );
  }
  return collation.name;
}

function formatMssql(collation: CollationSpec): string {
  rejectCharsetOutsideMysql('mssql', collation);
  return `COLLATE ${requireName('mssql', collation)}`;
}

function formatPgsql(collation: CollationSpec): string {
  rejectCharsetOutsideMysql('pgsql', collation);
  return `COLLATE "${requireName('pgsql', collation)}"`;
}

function formatMysql(collation: CollationSpec): string {
  const parts: string[] = [];
  if (collation.charset !== undefined) parts.push(`CHARACTER SET ${collation.charset}`);
  if (collation.name !== undefined) parts.push(`COLLATE ${collation.name}`);
  return parts.join(' ');
}

function formatOracle(collation: CollationSpec): string {
  rejectCharsetOutsideMysql('oracle', collation);
  return `COLLATE ${requireName('oracle', collation)}`;
}

function formatSqlite(collation: CollationSpec): string {
  rejectCharsetOutsideMysql('sqlite', collation);
  return `COLLATE ${requireName('sqlite', collation)}`;
}
